// Stand-in for the AI Configuration Engine until the backend endpoint exists.
// Returns the same response shape the real endpoint is expected to return so
// the action creators can be swapped to real API calls without UI changes.

import { summarizeBusinessHours } from 'pages/Onboarding/PhoneSystemStep/hoursUtils';

const SIMULATED_LATENCY_MS = 800;

// Stages are [name, target days in stage], matching the pipeline `stages[].days` field
const PIPELINES_BY_INDUSTRY = {
    insurance: {
        lead: { name: 'Insurance Leads', stages: [['New Lead', 1], ['Contacted', 2], ['Qualified', 3]] },
        opportunity: { name: 'Policy Sales', stages: [['Quote Requested', 2], ['Quoted', 5], ['Application Submitted', 7], ['Bound', 3]] }
    },
    real_estate: {
        lead: { name: 'Buyer & Seller Leads', stages: [['New Lead', 1], ['Contacted', 2], ['Appointment Set', 5]] },
        opportunity: { name: 'Deals', stages: [['Showing Scheduled', 7], ['Offer Made', 5], ['Under Contract', 30], ['Closed', 1]] }
    },
    home_services: {
        lead: { name: 'Service Requests', stages: [['New Request', 1], ['Contacted', 1], ['Estimate Scheduled', 3]] },
        opportunity: { name: 'Jobs', stages: [['Estimate Sent', 3], ['Approved', 2], ['Job Scheduled', 7], ['Completed', 1]] }
    },
    healthcare: {
        lead: { name: 'Patient Inquiries', stages: [['New Inquiry', 1], ['Contacted', 2], ['Consultation Booked', 7]] },
        opportunity: { name: 'Treatment Plans', stages: [['Consultation Done', 3], ['Plan Presented', 5], ['Plan Accepted', 7], ['Patient Onboarded', 1]] }
    },
    financial_services: {
        lead: { name: 'Prospects', stages: [['New Lead', 1], ['Discovery Call', 5], ['Qualified', 3]] },
        opportunity: { name: 'Client Onboarding', stages: [['Proposal Sent', 7], ['Documents Collected', 10], ['Accounts Opened', 5]] }
    },
    other: {
        lead: { name: 'Leads', stages: [['New Lead', 1], ['Contacted', 2], ['Qualified', 3]] },
        opportunity: { name: 'Sales', stages: [['Proposal Sent', 5], ['Negotiation', 7], ['Won', 1]] }
    }
};

const getStageNames = (pipeline) => pipeline.stages.map(([name]) => name).join(' → ');

const INDUSTRY_KEYWORDS = [
    { industry: 'insurance', keywords: ['insur', 'assurance', 'coverage', 'policy'] },
    { industry: 'real_estate', keywords: ['realty', 'realestate', 'homes', 'properties', 'estate', 'realtor'] },
    { industry: 'home_services', keywords: ['plumb', 'hvac', 'roof', 'electric', 'clean', 'landscap', 'repair', 'pest'] },
    { industry: 'healthcare', keywords: ['clinic', 'health', 'dental', 'medical', 'care', 'therapy', 'chiro'] },
    { industry: 'financial_services', keywords: ['bank', 'financ', 'capital', 'wealth', 'tax', 'invest', 'mortgage', 'lending'] }
];

const delay = (value) => new Promise(resolve => setTimeout(() => resolve(value), SIMULATED_LATENCY_MS));

const getDomain = (website = '') => {
    return website.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split(/[/?#]/)[0];
}

const toTitleCase = (value) => value.split(/[-_]/).filter(Boolean).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

const SERVICES_BY_INDUSTRY = {
    insurance: ['Auto insurance', 'Home insurance', 'Life insurance', 'Commercial insurance'],
    real_estate: ['Helping buyers find homes', 'Listing homes for sale', 'Free home valuations'],
    home_services: ['Repairs', 'Installations', 'Maintenance plans', 'Free estimates'],
    healthcare: ['New patient consultations', 'Preventive care', 'Most insurance plans accepted'],
    financial_services: ['Retirement planning', 'Investment management', 'Tax planning'],
    other: ['Consultations', 'Custom quotes']
};

const LISTED_HOURS_BY_INDUSTRY = {
    real_estate: 'Monday to Saturday, 9 AM to 6 PM',
    home_services: 'Monday to Saturday, 7 AM to 6 PM',
    healthcare: 'Monday to Friday, 8 AM to 5 PM'
};

const CONTACT_METHOD_NAMES = {
    phone: 'phone',
    contact_form: 'the contact form on our website',
    online_booking: 'booking online',
    live_chat: 'live chat'
};

// "a", "a or b", "a, b or c"
const joinWithOr = (items) => items.length > 1 ? `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}` : items.join('');

const hashString = (value) => [...value].reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) % 1000003, 7);

// Stand-in for an AI service that reads the website (browsers can't fetch other sites, so the real one runs on the backend)
const discoverWebsite = ({ website, industry }) => {
    const domain = getDomain(website);
    const [name] = domain.split('.');
    const match = INDUSTRY_KEYWORDS.find(({ keywords }) => keywords.some(keyword => domain.includes(keyword)));
    const category = industry && industry !== 'other' ? industry : (match?.industry || 'other');
    const hash = hashString(domain);

    const services = SERVICES_BY_INDUSTRY[category] || SERVICES_BY_INDUSTRY.other;
    const contact_methods = [
        'phone',
        hash % 3 !== 0 && 'contact_form',
        ['healthcare', 'home_services'].includes(category) && 'online_booking',
        hash % 4 === 0 && 'live_chat'
    ].filter(Boolean);
    const listed_hours = LISTED_HOURS_BY_INDUSTRY[category] || 'Monday to Friday, 9 AM to 5 PM';
    const methodsText = joinWithOr(contact_methods.map(method => CONTACT_METHOD_NAMES[method]));
    const summary = `Services: ${services.join(', ')}. Customers can reach us by ${methodsText}. Hours: ${listed_hours}.`;

    const suggestions = [];
    if (name) {
        suggestions.push({ field: 'business_name', value: toTitleCase(name), reason: `Based on the domain ${domain}` });
    }
    if (match) {
        suggestions.push({ field: 'industry', value: match.industry, reason: 'Keywords found on the website' });
    }
    suggestions.push({ field: 'description', value: summary, reason: 'Summary of your website' });

    return delay({
        website: domain,
        insights: { website: domain, services, contact_methods, listed_hours, summary },
        suggestions
    });
}

// Suggestions work in size bands; the wizard asks for an exact head count
const getTeamSizeBand = (team_size) => {
    const count = Number(team_size);
    if (!count) {
        return '1-5';
    }
    return count <= 5 ? '1-5' : count <= 20 ? '6-20' : '21+';
}

// Phrases in the goal details that point to a specific recommendation
const GOAL_CLUES = [
    { id: 'business_call_routing', pattern: /after hours|after \d|missed calls?|miss(ing)? calls|weekend|saturday|voicemail|answer/, reason: 'missed or after-hours calls' },
    { id: 'new_lead_text_response', pattern: /\btext|sms|respond (faster|quickly)|slow to respond|speed to lead/, reason: 'responding to leads faster' },
    { id: 'automated_follow_up', pattern: /follow[ -]?up|forget|nurture|quote|reminder|drip/, reason: 'following up' },
    { id: 'automatic_lead_assignment', pattern: /assign|distribut|round[ -]?robin|split|fair|who (gets|takes)/, reason: 'distributing leads' }
];

const getGoalClues = (goal_details = '') => {
    const text = goal_details.toLowerCase();
    return GOAL_CLUES.filter(({ pattern }) => pattern.test(text)).reduce((clues, { id, reason }) => ({ ...clues, [id]: `you mentioned ${reason}` }), {});
}

// What the website analysis found that points to a recommendation
const getWebsiteClues = (insights) => {
    const methods = insights?.contact_methods || [];
    const clues = {};
    if (methods.includes('contact_form')) {
        clues.new_lead_text_response = 'your website has a contact form, so leads expect a fast reply';
        clues.automatic_lead_assignment = "leads from your website's contact form need an owner right away";
    }
    if (methods.includes('live_chat')) {
        clues.new_lead_text_response = 'your website has live chat, so visitors expect quick answers';
    }
    if (methods.includes('online_booking')) {
        clues.automated_follow_up = 'your website takes online bookings, and reminders cut no-shows';
    }
    return clues;
}

const generateRecommendations = ({ industry = 'other', team_size, primary_goal, goal_details, website_insights }) => {
    const pipelines = PIPELINES_BY_INDUSTRY[industry] || PIPELINES_BY_INDUSTRY.other;
    const hasMultipleUsers = Number(team_size) > 1;

    const recommendations = [
        {
            id: 'lead_opportunity_pipelines',
            title: 'Lead & Opportunity Pipelines',
            summary: `A lead pipeline and an opportunity pipeline tailored to your industry.`,
            explanation: `${pipelines.lead.name}: ${getStageNames(pipelines.lead)}. ${pipelines.opportunity.name}: ${getStageNames(pipelines.opportunity)}. You can fine-tune the stages in CRM Configuration.`,
            tier: 'recommended',
            default_selected: true
        },
        {
            id: 'automatic_lead_assignment',
            title: 'Automatic Lead Assignment',
            summary: hasMultipleUsers ? 'Distribute new leads across your team with round-robin.' : 'Assign every new lead to you automatically.',
            explanation: 'Which users are included in the rotation and any caps per user will be confirmed during Team Setup.',
            tier: 'needs_confirmation',
            default_selected: hasMultipleUsers || primary_goal === 'manage_leads',
            config: { method: hasMultipleUsers ? 'round_robin' : 'single_owner' }
        },
        {
            id: 'business_call_routing',
            title: 'Business Call Routing',
            summary: 'Route calls to your team during business hours and to voicemail after hours.',
            explanation: 'Business hours are suggested for your industry in Phone System. A phone number must be provisioned and verified before routing is active.',
            tier: 'requires_activation',
            default_selected: primary_goal === 'improve_calling'
        },
        {
            id: 'new_lead_text_response',
            title: 'New Lead Text Response',
            summary: 'Send an instant text when a new lead comes in.',
            explanation: 'Includes a drafted welcome message with opt-out language. Texting requires carrier registration before messages can be sent.',
            tier: 'requires_activation',
            default_selected: primary_goal === 'manage_leads' || primary_goal === 'automate_follow_ups'
        },
        {
            id: 'automated_follow_up',
            title: 'Automated Follow-Up',
            summary: 'A follow-up sequence of calls, emails and texts for leads that have not responded.',
            explanation: 'Mixes calls, emails and texts over a lead\'s first 10 days. You can fine-tune it in Texting & Automation.',
            tier: 'recommended',
            default_selected: primary_goal === 'automate_follow_ups' || primary_goal === 'manage_leads'
        }
    ];

    // Goal details and the website can switch on recommendations the goal alone wouldn't; what the customer wrote wins
    const clues = { ...getWebsiteClues(website_insights), ...getGoalClues(goal_details) };
    return delay({
        recommendations: recommendations.map(recommendation => clues[recommendation.id] ? {
            ...recommendation,
            default_selected: true,
            explanation: `Because ${clues[recommendation.id]}. ${recommendation.explanation}`
        } : recommendation)
    });
}

const ROLE_PERMISSIONS = {
    owner: ['Full access to all settings, billing and data'],
    manager: ['Manage users and teams', 'View all leads, contacts and reports', 'Reassign leads', 'Listen to call recordings'],
    member: ['Work assigned leads and contacts', 'Make calls and send texts', 'View own activity and reports'],
    support: ['View and update contacts', 'Make calls and send texts', 'Create tasks and notes'],
    coordinator: ['View and update all records', 'Manage calendars and tasks', 'Upload documents']
};

const TEAM_STRUCTURE_BY_INDUSTRY = {
    insurance: {
        teams: [
            { id: 'sales_agents', name: 'Sales Agents', purpose: 'Work new leads and write policies.' },
            { id: 'customer_service', name: 'Customer Service', purpose: 'Handle renewals, changes and service calls.' }
        ],
        roles: [{ id: 'manager', name: 'Agency Manager' }, { id: 'member', name: 'Agent' }, { id: 'support', name: 'Customer Service Rep' }]
    },
    real_estate: {
        teams: [
            { id: 'buyer_agents', name: 'Buyer Agents', purpose: 'Work buyer leads and schedule showings.' },
            { id: 'listing_agents', name: 'Listing Agents', purpose: 'Work seller leads and manage listings.' },
            { id: 'transaction_coordination', name: 'Transaction Coordination', purpose: 'Manage deals from contract to close.' }
        ],
        roles: [{ id: 'manager', name: 'Broker' }, { id: 'member', name: 'Agent' }, { id: 'coordinator', name: 'Transaction Coordinator' }]
    },
    home_services: {
        teams: [
            { id: 'office', name: 'Office & Dispatch', purpose: 'Answer calls, book jobs and dispatch technicians.' },
            { id: 'estimators', name: 'Estimators', purpose: 'Visit customers and send estimates.' },
            { id: 'technicians', name: 'Field Technicians', purpose: 'Complete scheduled jobs.' }
        ],
        roles: [{ id: 'manager', name: 'Office Manager' }, { id: 'member', name: 'Estimator' }, { id: 'support', name: 'Dispatcher' }]
    },
    healthcare: {
        teams: [
            { id: 'front_desk', name: 'Front Desk', purpose: 'Answer calls and book consultations.' },
            { id: 'patient_coordinators', name: 'Patient Coordinators', purpose: 'Follow up with new patient inquiries.' }
        ],
        roles: [{ id: 'manager', name: 'Practice Manager' }, { id: 'member', name: 'Patient Coordinator' }, { id: 'support', name: 'Front Desk Staff' }]
    },
    financial_services: {
        teams: [
            { id: 'advisors', name: 'Advisors', purpose: 'Meet prospects and manage client relationships.' },
            { id: 'client_services', name: 'Client Services', purpose: 'Collect documents and handle client requests.' }
        ],
        roles: [{ id: 'manager', name: 'Managing Advisor' }, { id: 'member', name: 'Advisor' }, { id: 'support', name: 'Client Service Associate' }]
    },
    other: {
        teams: [
            { id: 'sales', name: 'Sales', purpose: 'Work new leads and close deals.' },
            { id: 'support', name: 'Support', purpose: 'Help existing customers.' }
        ],
        roles: [{ id: 'manager', name: 'Manager' }, { id: 'member', name: 'Sales Rep' }, { id: 'support', name: 'Support Rep' }]
    }
};


const generateTeamStructure = ({ industry = 'other', team_size, primary_goal }) => {
    const structure = TEAM_STRUCTURE_BY_INDUSTRY[industry] || TEAM_STRUCTURE_BY_INDUSTRY.other;
    const band = getTeamSizeBand(team_size);
    const isSmallTeam = band === '1-5';
    const isLargeTeam = band === '21+';

    const teams = structure.teams.map((team, index) => ({
        ...team,
        // Small teams usually work as a single group
        default_selected: !isSmallTeam || index === 0
    }));
    if (isLargeTeam) {
        teams.push({ id: 'management', name: 'Management', purpose: 'Oversee teams, assignments and reporting.', default_selected: true });
    }
    if (primary_goal === 'improve_calling' && !isSmallTeam) {
        teams.push({ id: 'inbound_calls', name: 'Inbound Calls', purpose: 'Answer incoming calls first so no caller waits.', default_selected: true });
    }

    const roles = [
        { id: 'owner', name: 'Owner', summary: 'That\'s you. Owners always have full access.', permissions: ROLE_PERMISSIONS.owner, locked: true, default_selected: true },
        ...structure.roles.map(({ id, name }) => ({
            id,
            name,
            summary: `${ROLE_PERMISSIONS[id].length} permissions tailored for this role.`,
            permissions: ROLE_PERMISSIONS[id],
            locked: false,
            // Small teams rarely need a separate manager role
            default_selected: !(isSmallTeam && id === 'manager')
        }))
    ];

    return delay({
        teams,
        roles,
        default_invite_role_id: 'member'
    });
}

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

// Accepts one person per line in formats like "Jane Doe <jane@acme.com>", "Jane Doe, jane@acme.com" or "jane.doe@acme.com"
const extractInvites = ({ text = '' }) => {
    const invites = text.split(/[\n;]+/).map(line => line.trim()).filter(Boolean).reduce((result, line) => {
        const [email] = line.match(EMAIL_PATTERN) || [];
        if (!email) {
            return result;
        }
        let name = line.replace(email, '').replace(/[<>(),"]/g, ' ').replace(/\s+/g, ' ').trim();
        if (!name) {
            name = email.split('@')[0].replace(/[._-]+/g, ' ').replace(/\d+/g, '').trim();
        }
        const [first_name = '', ...rest] = name.split(' ');
        result.push({
            first_name: toTitleCase(first_name),
            last_name: toTitleCase(rest.join('-')),
            email: email.toLowerCase()
        });
        return result;
    }, []);

    return delay({ invites });
}

const HOUR = 3600;

const BUSINESS_HOURS_BY_INDUSTRY = {
    insurance: { days: [1, 2, 3, 4, 5], start: 9, end: 17, reason: 'Typical hours for insurance agencies.' },
    real_estate: { days: [1, 2, 3, 4, 5, 6], start: 9, end: 18, reason: 'Real estate clients often call on Saturdays.' },
    home_services: { days: [1, 2, 3, 4, 5, 6], start: 7, end: 18, reason: 'Home service customers call early and on Saturdays.' },
    healthcare: { days: [1, 2, 3, 4, 5], start: 8, end: 17, reason: 'Typical front desk hours for practices.' },
    financial_services: { days: [1, 2, 3, 4, 5], start: 9, end: 17, reason: 'Typical hours for financial offices.' },
    other: { days: [1, 2, 3, 4, 5], start: 8, end: 17, reason: 'Standard business hours.' }
};

const draftGreetings = ({ business_name, hours_summary, ivr_options = [] }) => {
    const name = business_name || 'our office';
    const menu = ivr_options.map(({ digit, team_name }) => `For ${team_name}, press ${digit}.`).join(' ');
    return delay({
        ivr_greeting_script: `Thank you for calling ${name}. ${menu} To leave a message, stay on the line.`.replace(/\s+/g, ' ').trim(),
        voicemail_greeting_script: `You've reached ${name}. We can't take your call right now. Our hours are ${hours_summary}. Please leave your name, number and a short message and we'll call you back as soon as possible.`
    });
}

const generatePhoneSetup = async ({ industry = 'other', team_size, business_name, teams = [] }) => {
    const hours = BUSINESS_HOURS_BY_INDUSTRY[industry] || BUSINESS_HOURS_BY_INDUSTRY.other;
    const business_hours = [0, 1, 2, 3, 4, 5, 6].map(day => ({
        day,
        active: hours.days.includes(day),
        seconds_start: hours.start * HOUR,
        seconds_end: hours.end * HOUR
    }));

    const inboundTeam = teams.find(({ id }) => id === 'inbound_calls') || teams[0];
    // Up to 9 menu options, one per team, skipping management teams callers wouldn't ask for
    const ivrTeams = teams.filter(({ id }) => id !== 'management').slice(0, 9);
    const ivr_options = ivrTeams.map(({ id, name }, index) => ({ digit: String(index + 1), team_id: id, team_name: name }));
    const useIvr = ivrTeams.length >= 2;

    const greetings = await draftGreetings({ business_name, hours_summary: summarizeBusinessHours(business_hours), ivr_options: useIvr ? ivr_options : [] });

    return {
        business_hours,
        explanations: {
            business_hours: hours.reason,
            routing: useIvr
                ? 'You have more than one team, so a short menu lets callers reach the right one.'
                : inboundTeam?.id === 'everyone' ? 'All calls ring everyone on your account.' : `All calls ring your ${inboundTeam?.name || 'team'} team.`,
            ring_strategy: getTeamSizeBand(team_size) === '1-5'
                ? 'With a small team, ringing everyone at once answers calls fastest.'
                : 'Round robin spreads calls evenly across your team.'
        },
        routing: {
            team_id: inboundTeam?.id || '',
            ring_strategy: getTeamSizeBand(team_size) === '1-5' ? 'ringall' : 'roundrobin',
            ring_time: 30,
            no_answer_destination: 'company_voicemail'
        },
        ivr: {
            enabled: useIvr,
            greeting_script: greetings.ivr_greeting_script,
            options: ivr_options.map(({ digit, team_id }) => ({ digit, team_id }))
        },
        voicemail: {
            greeting_script: greetings.voicemail_greeting_script
        }
    };
}

// Field types are values from shared/constants/FIELD_TYPE
const CUSTOM_FIELDS_BY_INDUSTRY = {
    insurance: {
        group_name: 'Insurance Details',
        fields: [
            { label: 'Policy Type', field_type: 'dropdown', options: ['Auto', 'Home', 'Life', 'Health', 'Commercial'] },
            { label: 'Current Carrier', field_type: 'text' },
            { label: 'Renewal Date', field_type: 'date' },
            { label: 'Current Premium', field_type: 'currency' }
        ]
    },
    real_estate: {
        group_name: 'Property Preferences',
        fields: [
            { label: 'Buying or Selling', field_type: 'dropdown', options: ['Buying', 'Selling', 'Both'] },
            { label: 'Budget', field_type: 'currency' },
            { label: 'Preferred Areas', field_type: 'text' },
            { label: 'Pre-Approved', field_type: 'dropdown', options: ['Yes', 'No', 'In Progress'] }
        ]
    },
    home_services: {
        group_name: 'Service Details',
        fields: [
            { label: 'Service Needed', field_type: 'dropdown', options: ['Repair', 'Installation', 'Maintenance', 'Inspection'] },
            { label: 'Property Type', field_type: 'dropdown', options: ['Residential', 'Commercial'] },
            { label: 'Preferred Visit Time', field_type: 'dropdown', options: ['Morning', 'Afternoon', 'Evening'] },
            { label: 'Access Instructions', field_type: 'text' }
        ]
    },
    healthcare: {
        // Deliberately avoids medical details; those belong in a clinical system, not the CRM
        group_name: 'Patient Preferences',
        fields: [
            { label: 'Insurance Provider', field_type: 'text' },
            { label: 'Preferred Location', field_type: 'text' },
            { label: 'Preferred Contact Method', field_type: 'dropdown', options: ['Phone', 'Text', 'Email'] }
        ]
    },
    financial_services: {
        group_name: 'Financial Profile',
        fields: [
            { label: 'Service Interest', field_type: 'dropdown', options: ['Retirement', 'Investments', 'Insurance', 'Tax Planning'] },
            { label: 'Investable Assets', field_type: 'dropdown', options: ['Under $100k', '$100k-$500k', '$500k-$1M', 'Over $1M'] },
            { label: 'Target Retirement Date', field_type: 'date' }
        ]
    },
    other: {
        group_name: 'Additional Details',
        fields: [
            { label: 'Interested In', field_type: 'text' },
            { label: 'Budget', field_type: 'currency' },
            { label: 'Decision Timeline', field_type: 'dropdown', options: ['This month', '1-3 months', '3+ months'] }
        ]
    }
};

const toKey = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const generateCrmSetup = ({ industry = 'other', primary_goal, include_pipelines = true }) => {
    const pipelines = PIPELINES_BY_INDUSTRY[industry] || PIPELINES_BY_INDUSTRY.other;
    const customFields = CUSTOM_FIELDS_BY_INDUSTRY[industry] || CUSTOM_FIELDS_BY_INDUSTRY.other;

    const toPipeline = (account_stage, { name, stages }) => ({
        account_stage,
        included: include_pipelines,
        name,
        stages: stages.map(([stageName, days]) => ({ id: `${account_stage}_${toKey(stageName)}`, name: stageName, days }))
    });

    const fields = customFields.fields.map(({ label, field_type, options = [] }) => ({
        id: toKey(label),
        label,
        field_type,
        options,
        selected: true,
        is_custom: false
    }));
    if (primary_goal === 'manage_leads') {
        fields.push({ id: 'lead_rating', label: 'Lead Rating', field_type: 'dropdown', options: ['Hot', 'Warm', 'Cold'], selected: true, is_custom: false });
    }

    return delay({
        pipelines: {
            lead: toPipeline('lead', pipelines.lead),
            opportunity: toPipeline('opportunity', pipelines.opportunity)
        },
        field_group: { name: customFields.group_name, fields },
        explanations: {
            pipelines: 'Leads move through the lead pipeline until they are qualified, then become opportunities you work to close. Target days help flag records that are stuck.',
            fields: `Common details ${industry === 'other' ? 'businesses' : 'businesses like yours'} track on contacts, beyond name, phone and email.`
        }
    });
}

// How each industry talks about the next step with a new lead
const MESSAGING_TOPICS_BY_INDUSTRY = {
    insurance: { offer: 'get you a quote', topic: 'your insurance quote', appointment: 'quote review' },
    real_estate: { offer: 'help with your home search', topic: 'your home search', appointment: 'showing' },
    home_services: { offer: 'schedule your estimate', topic: 'your service request', appointment: 'appointment' },
    healthcare: { offer: 'book your consultation', topic: 'your consultation', appointment: 'consultation' },
    financial_services: { offer: 'set up a review of your goals', topic: 'your financial review', appointment: 'review meeting' },
    other: { offer: 'help with your request', topic: 'your request', appointment: 'appointment' }
};

const TEN_AM = 10 * HOUR;

const generateMessagingSetup = ({ industry = 'other', include_new_lead_text = true, include_follow_up = true }) => {
    const { offer, topic, appointment } = MESSAGING_TOPICS_BY_INDUSTRY[industry] || MESSAGING_TOPICS_BY_INDUSTRY.other;

    const text_templates = [
        {
            id: 'new_lead_welcome',
            name: 'New Lead Welcome',
            purpose: 'Sent right after a new lead comes in. Includes opt-out language, which carriers require on the first message.',
            message: `Hi %%First Name%%, it's %%My First Name%% at %%My Company%%. Thanks for reaching out! I'd love to ${offer}. Reply STOP to opt out.`,
            selected: include_new_lead_text
        },
        {
            id: 'follow_up_text',
            name: 'Follow-Up Check-In',
            purpose: 'A short nudge for leads who haven\'t replied.',
            message: `Hi %%First Name%%, just checking in about ${topic}. Reply here or call me at %%My Phone%%. - %%My First Name%%`,
            selected: include_follow_up
        },
        {
            id: 'appointment_reminder',
            name: 'Appointment Reminder',
            purpose: 'Send manually before a scheduled appointment.',
            message: `Hi %%First Name%%, a reminder about your ${appointment} with %%My Company%%. Reply here if you need to reschedule.`,
            selected: true
        }
    ];

    const email_templates = [
        {
            id: 'welcome_email',
            name: 'Welcome Email',
            purpose: 'Introduces you and sets expectations after the first contact.',
            subject: 'Thanks for contacting %%My Company%%',
            body: `Hi %%First Name%%,\n\nThank you for reaching out to %%My Company%%. I'm %%My Full Name%% and I'll be helping you with ${topic}.\n\nI'd love to ${offer}. You can reply to this email or call me directly at %%My Phone%%.\n\nTalk soon,\n%%My Full Name%%`,
            selected: true
        },
        {
            id: 'follow_up_email',
            name: 'Follow-Up Email',
            purpose: 'A last follow-up for leads who haven\'t responded.',
            subject: `Following up on ${topic}`,
            body: `Hi %%First Name%%,\n\nI wanted to follow up on ${topic}. If now isn't a good time, just let me know when works better and I'll reach out then.\n\nBest,\n%%My Full Name%%\n%%My Company%% | %%My Phone%%`,
            selected: include_follow_up
        }
    ];

    // Cadence matches the Automated Follow-Up recommendation, with an instant text first when that was chosen
    const steps = [
        include_new_lead_text && { type: 'text', deliver: 'instant', timeamount: '', time: 0, template_id: 'new_lead_welcome' },
        { type: 'call', deliver: 'days', timeamount: 1, time: TEN_AM, template_id: '' },
        { type: 'email', deliver: 'days', timeamount: 1, time: TEN_AM, template_id: 'welcome_email' },
        { type: 'text', deliver: 'days', timeamount: 2, time: TEN_AM, template_id: 'follow_up_text' },
        { type: 'call', deliver: 'days', timeamount: 3, time: TEN_AM, template_id: '' },
        { type: 'email', deliver: 'days', timeamount: 3, time: TEN_AM, template_id: 'follow_up_email' }
    ].filter(Boolean).map((step, index) => ({ ...step, id: `step_${index + 1}` }));

    return delay({
        text_templates,
        email_templates,
        sequence: {
            included: include_follow_up || include_new_lead_text,
            name: 'New Lead Follow-Up',
            description: 'Calls, texts and emails for new leads over their first 10 days.',
            saturday: false,
            sunday: false,
            steps
        },
        explanations: {
            templates: 'Drafted for your industry with merge fields, so each message is personalized automatically.',
            sequence: 'Most leads respond within the first few touches, so the follow-ups are front-loaded and mix calls, texts and emails.'
        }
    });
}

// Header aliases per import field, compared after stripping everything but letters and digits
const IMPORT_FIELD_ALIASES = {
    first_name: ['firstname', 'first', 'fname', 'givenname'],
    last_name: ['lastname', 'last', 'lname', 'surname', 'familyname'],
    full_name: ['name', 'fullname', 'contactname', 'customername', 'clientname'],
    email: ['email', 'emailaddress', 'mail', 'primaryemail'],
    phone: ['phone', 'phonenumber', 'telephone', 'tel', 'workphone', 'homephone', 'officephone'],
    mobile_phone: ['mobile', 'cell', 'cellphone', 'mobilephone', 'mobilenumber'],
    company: ['company', 'companyname', 'business', 'businessname', 'organization', 'account', 'accountname'],
    job_title: ['title', 'jobtitle', 'position'],
    street: ['address', 'street', 'address1', 'streetaddress', 'addressline1'],
    city: ['city', 'town'],
    state: ['state', 'province', 'region', 'st'],
    zip: ['zip', 'zipcode', 'postalcode', 'postcode'],
    source: ['source', 'leadsource', 'channel', 'origin'],
    notes: ['notes', 'note', 'comments', 'comment']
};

const normalizeHeader = (value = '') => String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
const IMPORT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const getPhoneDigits = (value = '') => String(value).replace(/\D/g, '').slice(-10);

const inferFromValues = (values) => {
    const filled = values.filter(value => String(value || '').trim());
    if (!filled.length) {
        return null;
    }
    const share = (test) => filled.filter(test).length / filled.length;
    if (share(value => IMPORT_EMAIL_PATTERN.test(String(value).trim())) >= 0.8) {
        return { dyl_field: 'email', reason: 'Values look like email addresses' };
    }
    if (share(value => getPhoneDigits(value).length === 10) >= 0.8) {
        return { dyl_field: 'phone', reason: 'Values look like phone numbers' };
    }
    return null;
}

// Matches each column to a DYL field: exact alias (high), partial alias (medium), or by its values (medium)
const suggestColumnMapping = ({ headers = [], sample_rows = [], custom_fields = [] }) => {
    const used = new Set();
    const customTargets = custom_fields.map(({ value, text }) => ({ dyl_field: value, aliases: [normalizeHeader(text)] }));
    const standardTargets = Object.entries(IMPORT_FIELD_ALIASES).map(([dyl_field, aliases]) => ({ dyl_field, aliases }));
    const targets = [...customTargets, ...standardTargets];

    const mappings = headers.map((header, index) => {
        const normalized = normalizeHeader(header);
        const exact = targets.find(({ dyl_field, aliases }) => !used.has(dyl_field) && aliases.includes(normalized));
        const partial = !exact && normalized.length > 2 && targets.find(({ dyl_field, aliases }) => !used.has(dyl_field) && aliases.some(alias => alias.length > 2 && (normalized.includes(alias) || alias.includes(normalized))));
        const inferred = !exact && !partial && inferFromValues(sample_rows.map(row => row[index]));
        const match = exact
            ? { dyl_field: exact.dyl_field, confidence: 'high', reason: 'Column name matches' }
            : partial
                ? { dyl_field: partial.dyl_field, confidence: 'medium', reason: 'Column name is similar' }
                : inferred && !used.has(inferred.dyl_field)
                    ? { ...inferred, confidence: 'medium' }
                    : { dyl_field: '', confidence: 'none', reason: 'No matching field found' };
        if (match.dyl_field) {
            used.add(match.dyl_field);
        }
        return { header, ...match };
    });

    const normalizedHeaders = headers.map(normalizeHeader).join(' ');
    const looksLikeCustomers = /policy|customersince|accountnumber|renewal|purchase|client/.test(normalizedHeaders);

    return delay({
        mappings,
        suggested_module: looksLikeCustomers ? 'Customer' : 'Lead',
        module_reason: looksLikeCustomers
            ? 'Columns like policy, renewal or account number suggest these are existing customers.'
            : 'No customer-only columns found, so these look like leads to work through your pipeline.'
    });
}

// Finds likely duplicates inside the file (same email, same phone, or same name and zip) plus data problems
const analyzeImportData = ({ rows = [], mappings = [] }) => {
    const columnOf = (field) => mappings.findIndex(({ dyl_field }) => dyl_field === field);
    const value = (row, field) => {
        const index = columnOf(field);
        return index === -1 ? '' : String(row[index] ?? '').trim();
    }
    const getDisplayName = (row) => (value(row, 'full_name') || `${value(row, 'first_name')} ${value(row, 'last_name')}`).trim();
    const getName = (row) => getDisplayName(row).toLowerCase();

    // Union-find so rows connected by any shared key end up in one group
    const parent = rows.map((_, index) => index);
    const find = (index) => parent[index] === index ? index : (parent[index] = find(parent[index]));
    const reasons = {};
    const seen = {};
    const link = (key, index, reason) => {
        if (seen[key] === undefined) {
            seen[key] = index;
            return;
        }
        const root = find(seen[key]);
        parent[find(index)] = root;
        reasons[root] = reasons[root] || reason;
    }

    let invalid_emails = 0;
    let invalid_phones = 0;
    let missing_names = 0;
    rows.forEach((row, index) => {
        const email = value(row, 'email').toLowerCase();
        const phone = getPhoneDigits(value(row, 'phone') || value(row, 'mobile_phone'));
        const name = getName(row);
        if (email && !IMPORT_EMAIL_PATTERN.test(email)) invalid_emails++;
        if ((value(row, 'phone') || value(row, 'mobile_phone')) && phone.length !== 10) invalid_phones++;
        if (!name) missing_names++;
        if (email && IMPORT_EMAIL_PATTERN.test(email)) link(`email:${email}`, index, 'Same email');
        if (phone.length === 10) link(`phone:${phone}`, index, 'Same phone');
        if (name && value(row, 'zip')) link(`name:${name}|${value(row, 'zip')}`, index, 'Same name and zip');
    });

    const groups = Object.values(rows.reduce((result, _, index) => {
        const root = find(index);
        result[root] = [...(result[root] || []), index];
        return result;
    }, {})).filter(group => group.length > 1);

    return delay({
        total_rows: rows.length,
        duplicate_groups: groups.slice(0, 20).map(group => ({
            rows: group.map(index => index + 2), // spreadsheet row numbers, counting the header row
            label: getDisplayName(rows[group[0]]) || value(rows[group[0]], 'email') || 'Unnamed record',
            reason: reasons[find(group[0])] || 'Same details'
        })),
        duplicate_group_count: groups.length,
        duplicate_row_count: groups.reduce((count, group) => count + group.length - 1, 0),
        invalid_emails,
        invalid_phones,
        missing_names
    });
}

const SAMPLE_IMPORT_ROWS = [
    ['Jane', 'Doe', 'jane.doe@example.com', '(818) 555-0142', 'Main St 12', 'Burbank', 'CA', '91502', 'Website'],
    ['John', 'Smith', 'john.smith@example.com', '818-555-0199', '45 Oak Ave', 'Glendale', 'CA', '91203', 'Referral'],
    ['Maria', 'Lopez', 'maria.lopez@example', '8185550110', '9 Pine Rd', 'Pasadena', 'CA', '91101', 'Facebook'],
    ['JANE', 'DOE', 'Jane.Doe@example.com', '', '', 'Burbank', 'CA', '91502', 'Walk-in'],
    ['Ahmed', 'Khan', 'ahmed.khan@example.com', '555-0123', '', 'Van Nuys', 'CA', '91401', 'Website'],
    ['Lisa', 'Wong', 'lisa.wong@example.com', '(818) 555-0175', '200 Elm St', 'Burbank', 'CA', '91505', 'Referral'],
    ['', '', 'no.name@example.com', '', '', '', '', '', 'Website'],
    ['Lisa', 'Wong', 'lwong@example.com', '818.555.0175', '200 Elm St', 'Burbank', 'CA', '91505', 'Website']
];

const getSampleImportFile = () => delay({
    file_name: 'sample_contacts.csv',
    headers: ['First Name', 'Last Name', 'E-mail', 'Cell', 'Street Address', 'City', 'ST', 'Postal Code', 'Lead Source'],
    rows: SAMPLE_IMPORT_ROWS
});

const LEAD_VENDORS_BY_INDUSTRY = {
    insurance: ['EverQuote', 'SmartFinancial', 'QuoteWizard', 'MediaAlpha'],
    real_estate: ['Zillow', 'Realtor.com', 'Redfin', 'Homes.com'],
    home_services: ['Angi', 'Thumbtack', 'HomeAdvisor', 'Yelp'],
    healthcare: ['Zocdoc', 'Healthgrades'],
    financial_services: ['SmartAsset', 'Zoe Financial'],
    other: []
};

const generateIntegrationSuggestions = ({ industry = 'other', website, business_name }) => delay({
    email_provider: '',
    sync_calendar: true,
    web_form: {
        enabled: !!website,
        name: `${business_name || 'Website'} Contact Form`
    },
    lead_vendors: {
        enabled: false,
        names: [],
        suggestions: LEAD_VENDORS_BY_INDUSTRY[industry] || []
    },
    email_parsing: { enabled: false },
    explanations: {
        web_form: website
            ? `You have a website, so a contact form on it can send leads straight into DYL.`
            : 'A contact form you can put on any website or landing page.',
        lead_vendors: (LEAD_VENDORS_BY_INDUSTRY[industry] || []).length
            ? `Common lead sources for your industry are listed below. Pick the ones you use.`
            : 'Add any companies you buy leads from.'
    }
});

export default {
    suggestColumnMapping,
    analyzeImportData,
    getSampleImportFile,
    generateIntegrationSuggestions,
    generateMessagingSetup,
    discoverWebsite,
    generateRecommendations,
    generateTeamStructure,
    extractInvites,
    generatePhoneSetup,
    draftGreetings,
    generateCrmSetup
};
